import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { analytics } from '../../analytics';
import { Box } from '../grid';
import { useEligibleGiftDetails } from '../../hooks/use-eligible-gift-details';

import { GiftPicker } from './gift-picker';
import { ProgressBar } from './progress-bar';
import { QualifyingMessage } from './qualifying-message';
import { SelectedGift } from './selected-gift';
import { ThresholdLost } from './threshold-lost';

const Wrapper = styled(Box)`
  width: 100%;
  /* Space below the slot before the order items. Owned by the component so it
     only applies when content actually renders — the call site can't reliably
     predict the slot's null states (e.g. surprise mode before qualifying). */
  margin-bottom: 16px;
`;

/**
 * W1 — Surprise & Delight slot (container).
 *
 * Reads cart-store S&D state via `useRootStore()` and composes the right child
 * component (W2 ProgressBar / W3 QualifyingMessage / W5 GiftPicker /
 * W7 SelectedGift). Also fires W8 <ThresholdLost/> once when an eligible cart
 * transitions to not-eligible with a gift selected. Both criteria types share
 * the qualified-state card (W2 with the bar hidden for pluQuantity).
 *
 * The slot is gated entirely on API response data: `validateOrder` only
 * returns a `surpriseAndDelight` block (campaignId etc.) when the BE
 * `menu-free-items` flag has an applicable campaign, so the absence of a
 * campaignId doubles as the feature's off switch. Returns `null` when no
 * campaign applies.
 */
const SurpriseAndDelight = ({ onBrowseMenu }) => {
  const { CartStore, OrderStore } = useRootStore();

  const {
    surpriseAndDelightIsEligible: isEligible,
    surpriseAndDelightCampaignId: campaignId,
    surpriseAndDelightEligibleGiftPlus: eligibleGiftPlus,
    surpriseAndDelightCriteriaType: criteriaType,
    surpriseAndDelightShowBeforeQualifiedMessage: showBeforeQualifiedMessage,
    surpriseAndDelightMinSpend: minSpend,
    surpriseAndDelightMessaging: messaging,
    selectedGiftPlu,
    setSelectedGiftPlu,
  } = CartStore;

  const eligibleGiftPlusArray = eligibleGiftPlus ? eligibleGiftPlus.slice() : [];
  const giftDetailsByPlu = useEligibleGiftDetails(eligibleGiftPlusArray);

  // Track previous eligibility so we can fire <ThresholdLost/> once on the
  // qualified -> not-qualified transition while a gift was selected. The flag
  // sits in state (not just a ref) so the banner/modal renders on the next
  // commit, and the ref guarantees we only fire on the actual transition.
  const previousIsEligibleRef = useRef(isEligible);
  const [thresholdLostOpen, setThresholdLostOpen] = useState(false);

  // Qualified state: the gift list nests inside the (now-complete) progress bar
  // and can be collapsed via the "Choose gift" toggle. Expanded by default so
  // the customer sees their options the moment they qualify.
  const [giftListExpanded, setGiftListExpanded] = useState(true);

  useEffect(() => {
    const wasEligible = previousIsEligibleRef.current;
    if (wasEligible && !isEligible && selectedGiftPlu) {
      setThresholdLostOpen(true);
    }
    previousIsEligibleRef.current = isEligible;
  }, [isEligible, selectedGiftPlu]);

  // Reset the gift list to open whenever the cart is not eligible, so it shows
  // expanded again the next time the cart qualifies (CR #1329). A manual
  // collapse therefore only persists while eligibility is held continuously.
  useEffect(() => {
    if (!isEligible) {
      setGiftListExpanded(true);
    }
  }, [isEligible]);

  // Fire SURPRISE_OFFER_DISPLAYED once per campaign-active period — when the
  // slot first sees a campaignId, and again only after that campaignId clears
  // and a new one arrives.
  useEffect(() => {
    if (campaignId) {
      analytics.track(ANALYTICS_EVENTS.SURPRISE_OFFER_DISPLAYED, {
        campaignId,
        criteriaType,
      });
    }
  }, [campaignId, criteriaType]);

  // Cart subtotal in cents — drives the progress bar's fill ratio.
  const currentCents = get(OrderStore, 'subTotal.cents', 0);

  // The threshold-lost message is read from server messaging when present,
  // with a safe default — the slot always renders the W8 component so it can
  // still appear after isEligible flips back to false.
  const thresholdLostMessage = get(
    messaging,
    'thresholdLostMessage',
    'Some items were removed from your cart because your order no longer qualifies for the promo item.'
  );

  const thresholdLost = (
    <ThresholdLost
      isOpen={thresholdLostOpen}
      message={thresholdLostMessage}
      onClose={() => setThresholdLostOpen(false)}
    />
  );

  if (!campaignId) {
    // Surface only the (closed) threshold-lost shell — when it's open the
    // banner/modal stays mounted across the eligibility flip.
    return thresholdLostOpen ? <Wrapper>{thresholdLost}</Wrapper> : null;
  }

  // The cart slot renders only the qualifying/qualified messages — the
  // contract's third field (messaging.cta) is the constant tagline used by
  // the menu page's GlobalBanner, not the slot.
  const qualifyingMessage = get(messaging, 'qualifyingMessage', '');
  const qualifiedMessage = get(messaging, 'qualifiedMessage', '');

  // --- Not-eligible branch -------------------------------------------------
  if (!isEligible) {
    if (!showBeforeQualifiedMessage) {
      // V7 "surprise" mode — hide UI entirely until qualified.
      // The threshold-lost shell still gets to render so the transition
      // notification surfaces after a cart edit invalidates the campaign.
      return thresholdLostOpen ? <Wrapper>{thresholdLost}</Wrapper> : null;
    }

    if (criteriaType === 'minimumSpend') {
      return (
        <Wrapper data-testid="surprise-and-delight-slot">
          <ProgressBar
            currentCents={currentCents}
            targetCents={minSpend || 0}
            qualifyingMessage={qualifyingMessage}
            cta="Browse menu"
            onCtaClick={onBrowseMenu}
          />
          {thresholdLost}
        </Wrapper>
      );
    }

    if (criteriaType === 'pluQuantity') {
      return (
        <Wrapper data-testid="surprise-and-delight-slot">
          <QualifyingMessage qualifyingMessage={qualifyingMessage} />
          {thresholdLost}
        </Wrapper>
      );
    }

    // Unknown criteria type — still mount the threshold-lost shell so the
    // transition notification surfaces.
    return thresholdLostOpen ? <Wrapper>{thresholdLost}</Wrapper> : null;
  }

  // --- Eligible branch -----------------------------------------------------
  // The gift PICKER lives in this slot. Once a gift is chosen the slot collapses
  // to a non-interactive confirmation, and the chosen gift is rendered as a
  // "free gift" row at the top of the order list by cart-preview (reusing the
  // order row component), so it isn't duplicated here.
  const picker = (
    <GiftPicker
      eligibleGiftPlus={eligibleGiftPlusArray}
      giftDetailsByPlu={giftDetailsByPlu}
      selectedGiftPlu={selectedGiftPlu}
      onSelect={plu => setSelectedGiftPlu(plu)}
    />
  );

  // Once a gift is chosen the slot collapses to the confirmation banner —
  // identical for both criteria types.
  if (selectedGiftPlu) {
    const giftName = get(giftDetailsByPlu, `${selectedGiftPlu}.name`) || selectedGiftPlu;
    return (
      <Wrapper data-testid="surprise-and-delight-slot">
        <SelectedGift variant="banner" name={giftName} />
        {thresholdLost}
      </Wrapper>
    );
  }

  // Qualified, no selection: one shared card — qualified message + collapsed
  // gift list behind the "Choose gift" toggle. The only presentation
  // difference between criteria types is the progress track, which only
  // minimumSpend campaigns have a threshold to visualise.
  return (
    <Wrapper data-testid="surprise-and-delight-slot">
      <ProgressBar
        currentCents={currentCents}
        targetCents={minSpend || 0}
        qualifyingMessage={qualifiedMessage}
        cta="Choose gift"
        onCtaClick={() => setGiftListExpanded(expanded => !expanded)}
        ctaShowChevron
        expanded={giftListExpanded}
        showBar={criteriaType === 'minimumSpend'}
      >
        {giftListExpanded && picker}
      </ProgressBar>
      {thresholdLost}
    </Wrapper>
  );
};

SurpriseAndDelight.propTypes = {
  // Called when the user clicks the progress bar's "Browse menu" CTA — closes
  // the cart and returns to the menu so they can add items toward the threshold.
  onBrowseMenu: PropTypes.func,
};

SurpriseAndDelight.defaultProps = {
  onBrowseMenu: () => {},
};

const ComposedSurpriseAndDelight = observer(SurpriseAndDelight);

export { ComposedSurpriseAndDelight as SurpriseAndDelight };
